import React, { useEffect, useRef, useState } from "react";
import { LeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { Spin, Typography } from "antd";
import styles from "./institution-form.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import GeneralInformation from "./components/GeneralInformation/GeneralInformation";
import ContactInformation from "./components/ContactInformation/ContactInformation";
import RegulatoryAcademicInfo from "./components/RegulatoryAcademicInfo/RegulatoryAcademicInfo";
import AdministratorInformation from "./components/AdministratorInformation/AdministratorInformation";
import SupportingDocuments from "./components/SupportingDocuments/SupportingDocuments";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { createInstitution, getInstitutionDetails, updateInstitution } from "../../../../services/institution";
import CustomButton from "../../../../common/custom-button/custom-button";
import DeclarationForm from "./components/DeclarationForm/DeclarationForm";
import SVGIcon from "../../../../constants/SVGIcon";
import { toast } from "react-toastify";
import { DATE_FORMAT } from "../../../../constants/common";
import { validateDate } from "../../../../utils/format-date/format-date";

const INSTITUTION_FORMS = [
  {
    label: "School Details",
    Component: GeneralInformation,
  },
  {
    label: "Contact Information",
    Component: ContactInformation,
  },
  {
    label: "Regulatory & Academic Info",
    Component: RegulatoryAcademicInfo,
  },
  {
    label: "Administrator Information",
    Component: AdministratorInformation,
  },
  {
    label: "Supporting Documents",
    Component: SupportingDocuments,
  },
  {
    label: "Declaration",
    Component: DeclarationForm,
  },
];

const InstitutionForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const institutionId = params.get("uid");
  const [institutionDetails, setInstitutionDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const printRef = useRef();

  const filterCb = (_, idx) => {
    if (institutionDetails && idx === 3) return false;
    else return true;
  };

  const form1 = useForm({
    mode: "onChange",
    defaultValues: {
      institution_name: "",
      prinicipal_first_name: "",
      prinicipal_middle_name: "",
      prinicipal_last_name: "",
      deputy_prinicipal_first_name: "",
      deputy_prinicipal_middle_name: "",
      deputy_prinicipal_last_name: "",
      registration_number: "",
      curriculum: "",
      school_type: "",
      ownership_type: "",
      established_date: "",
      vision: "",
      institution_logo: "",
      linked_school_id: "",
    },
  });
  const form2 = useForm({
    mode: "onChange",
    defaultValues: {
      physical_address: "",
      lat: "",
      lng: "",
      postal_address: "",
      province: "",
      country: "",
      principal_phone_number: "",
      principal_email_address: "",
      deputy_principal_phone_number: "",
      deputy_principal_email_address: "",
      school_phone_number: "",
      school_email: "",
      school_website_url: "",
    },
  });

  const form3 = useForm({
    mode: "onChange",
    defaultValues: {
      education_authority: "",
      levels_offered: "",
      total_students: "",
      total_teachers: "",
      total_classes: "",
      total_support_staffs: "",
      boarding_or_day: "",
    },
  });

  const form4 = useForm({
    mode: "onChange",
    defaultValues: {
      full_name: "",
      designation: "",
      national_id: "",
      email: "",
      phone_number: "",
      administrator_picture: ""
    },
  });

  const form5 = useForm({
    mode: "onChange",
    defaultValues: {
      accrediation_certificate: "",
      operating_license: "",
      additional_document: "",
    },
  });

  const form6 = useForm({
    mode: "onChange",
    defaultValues: {
      declaration: "",
      digital_signature: "",
    },
  });

  const FORM_SET = [form1, form2, form3, form4, form5, form6].filter(filterCb);

  useEffect(() => {
    if (institutionId) {
      getInstitutionData();
    } else {
      setInstitutionDetails(null);
    }
  }, [institutionId]);

  const getInstitutionData = async () => {
    try {
      setLoading(true);
      const response = await getInstitutionDetails(institutionId);
      if (response?.data) {
        const data = response?.data?.data;
        setInstitutionDetails(data);
        populateFormData(data);
      }
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  const getImageName = (img) => {
    const imageName = img
      ? decodeURIComponent(img)
        ?.split("#")
        ?.slice(-1)?.[0] ?? null
      : null;
    return imageName ? { name: imageName } : {}
  };

  const populateFormData = (data) => {
    form1.reset({
      institution_name: data?.institution_name ?? "",
      prinicipal_first_name: data?.prinicipal_first_name ?? "",
      prinicipal_middle_name: data?.prinicipal_middle_name ?? "",
      prinicipal_last_name: data?.prinicipal_last_name ?? "",
      deputy_prinicipal_first_name: data?.deputy_prinicipal_first_name ?? "",
      deputy_prinicipal_middle_name: data?.deputy_prinicipal_middle_name ?? "",
      deputy_prinicipal_last_name: data?.deputy_prinicipal_last_name ?? "",
      registration_number: data?.registration_number ?? "",
      curriculum: data?.curriculum ?? "",
      school_type: data?.school_type ?? "",
      ownership_type: data?.ownership_type ?? "",
      established_date: data?.established_date 
        ? validateDate(data?.established_date)
        : "",
      institution_logo: getImageName(data?.institution_logo),
      vision: data?.vision ?? "",
      linked_school_id: data?.parent_id ?? "",
    });

    form2.reset({
      physical_address: data?.physical_address ?? "",
      postal_address: data?.postal_address ?? "",
      province: data?.province ?? "",
      country: data?.country ?? "Zimbabwe",
      principal_phone_number: data?.principal_phone_number ?? "",
      principal_email_address: data?.principal_email_address ?? "",
      deputy_principal_phone_number: data?.deputy_principal_phone_number ?? "",
      deputy_principal_email_address: data?.deputy_principal_email_address ?? "",
      school_phone_number: data?.school_phone_number ?? "",
      school_email: data?.school_email ?? "",
      school_website_url: data?.school_website_url ?? "",
      lat: data?.latitude ?? "",
      lng: data?.longitude ?? "",
    });

    form3.reset({
      education_authority: data?.education_authority ?? "",
      levels_offered: data?.levels_offered ?? "",
      total_students: data?.total_students ?? "",
      total_teachers: data?.total_teachers ?? "",
      total_classes: data?.total_classes ?? "",
      total_support_staffs: data?.total_support_staffs ?? "",
      boarding_or_day: data?.boarding_or_day ?? "",
    });

    form5.reset({
      accrediation_certificate: getImageName(data?.accrediation_certificate),
      operating_license: getImageName(data?.operating_license),
      additional_document: getImageName(data?.additional_document),
    });

    form6.reset({
      digital_signature: data?.digital_signature ?? "",
    });
  };

  const handleSubmit = async () => {
    try {
      const form1Values = form1.getValues();
      const form2Values = form2.getValues();
      const form3Values = form3.getValues();
      const form4Values = form4.getValues();
      const form5Values = form5.getValues();
      const form6Values = form6.getValues();

      const formdata = new FormData();
      // Form 1
      formdata.append("institution_name", form1Values.institution_name);
      formdata.append("prinicipal_first_name", form1Values.prinicipal_first_name);
      formdata.append("prinicipal_middle_name", form1Values.prinicipal_middle_name);
      formdata.append("prinicipal_last_name", form1Values.prinicipal_last_name);
      formdata.append("deputy_prinicipal_first_name", form1Values.deputy_prinicipal_first_name);
      formdata.append("deputy_prinicipal_middle_name", form1Values.deputy_prinicipal_middle_name);
      formdata.append("deputy_prinicipal_last_name", form1Values.deputy_prinicipal_last_name);
      formdata.append("registration_number", form1Values.registration_number);
      formdata.append("curriculum", form1Values.curriculum);
      formdata.append("school_type", form1Values.school_type);
      formdata.append("ownership_type", form1Values.ownership_type);
      formdata.append("vision", form1Values.vision);
      if(form1Values.linked_school_id){
        formdata.append("parent_id", form1Values.linked_school_id);
      }

      if (form1Values.institution_logo?.size) {
        formdata.append("institution_logo", form1Values.institution_logo);
      }

      formdata.append(
        "established_date",
        form1Values.established_date ? dayjs(form1Values.established_date).format(DATE_FORMAT.DD_MM_YYYY) : ""
      );

      // Form 2
      formdata.append("physical_address", form2Values.physical_address);
      formdata.append("postal_address", form2Values.postal_address);
      formdata.append("province", form2Values.province);
      formdata.append("country", form2Values.country);
      formdata.append("principal_phone_number", form2Values.principal_phone_number);
      formdata.append("principal_email_address", form2Values.principal_email_address);
      formdata.append("deputy_principal_phone_number", form2Values.deputy_principal_phone_number);
      formdata.append("deputy_principal_email_address", form2Values.deputy_principal_email_address);
      formdata.append("school_phone_number", form2Values.school_phone_number);
      formdata.append("school_email", form2Values.school_email);
      formdata.append("school_website_url", form2Values.school_website_url);

      formdata.append("longitude", form2Values.lng);
      formdata.append("latitude", form2Values.lat);

      // Form 3
      formdata.append("education_authority", form3Values.education_authority);
      formdata.append("levels_offered", form3Values.levels_offered);
      formdata.append("total_students", form3Values.total_students);
      formdata.append("total_teachers", form3Values.total_teachers);
      formdata.append("total_classes", form3Values.total_classes);
      formdata.append("total_support_staffs", form3Values.total_support_staffs);
      formdata.append("boarding_or_day", form3Values.boarding_or_day);

      // Form 4
      const { administrator_picture, ...restForm4Values } = form4Values;
      const metaObject = {
        ...(!institutionDetails ? { ...restForm4Values } : {}),
      };
      if (Object.keys(metaObject).length) {
        formdata.append("administrator", JSON.stringify(metaObject));
      }
      if (!institutionDetails && administrator_picture?.size) {
        formdata.append("administrator_picture", administrator_picture);
      }

      // Form 5
      if (form5Values.accrediation_certificate?.size) {
        formdata.append("accrediation_certificate", form5Values.accrediation_certificate);
      }
      if (form5Values.operating_license?.size) {
        formdata.append("operating_license", form5Values.operating_license);
      }
      if (form5Values.additional_document?.size) {
        formdata.append("additional_document", form5Values.additional_document);
      }

      // Form 6
      formdata.append("digital_signature", form6Values.digital_signature);

      setLoading(true);
      if (institutionDetails) {
        await updateInstitution(institutionDetails?.id, formdata);
        toast.success("Institution/School updated successfully!");
      } else {
        await createInstitution(formdata);
        toast.success("Institution/School added successfully!");
      }
      setIsSaved(true);
    } catch (err) {
      console.log(err?.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInstitute = async () => {
    try {
      const results = await Promise.all([
        form1.trigger(),
        form2.trigger(),
        form3.trigger(),
        form4.trigger(),
        form5.trigger(),
        form6.trigger(),
      ]);

      const allValid = results.every(Boolean);
      if (allValid) {
        await handleSubmit();
      } else {
        const form2Values = form2.getValues();
        if (form2Values.physical_address && (!form2Values.lat || !form2Values.lng)) {
          toast.error("Please select a valid physical address from the dropdown to set location coordinates.");
          return;
        }
        toast.error("Some fields need your attention. Please review and try again.");
      }
    } catch (err) {
      console.error( err);
    }
  };

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=800,height=600");
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map(style => style.outerHTML)
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Institute Details</title>
          ${styles}
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.leftSection} onClick={() => navigate(-1)}>
        <LeftOutlined />
        <Typography className={styles.navTitle}>
          {institutionId ? "Edit Institution/School" : "Add new Institution/School"}
        </Typography>
      </div>
      <div className={styles.institutionForm}>
        <div ref={printRef}>
          {INSTITUTION_FORMS?.filter(filterCb)?.map((item, index) => {
            const Component = item?.Component;
            return (
              <div className={styles.formComponentWrapper}>
                <div className={styles.formTitle}>{item?.label}</div>
                <Component {...FORM_SET[index]} />
              </div>
            )
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          {isSaved ? (
            <>
              <CustomButton
                onClick={() => navigate(-1)}
                title="Back"
                variant="outlined"
                style={{
                  width: "95px",
                  backgroundColor: "transparent",
                  color: "#4A4A4A",
                  border: "1px solid #888888",
                }}
              />
              <CustomButton
                onClick={handlePrint}
                title={"Print as PDF"}
                variant="outlined"
                icon={
                  <PrinterOutlined />
                }
              />
            </>
          ) : (
            <CustomButton
              onClick={handleSaveInstitute}
              title={institutionId ? "Update School" : "Add School"}
              type="submit"
              variant="outlined"
              icon={
                <SVGIcon
                  name={institutionId ? "edit-icon" : "add-icon"}
                  width={"20"}
                  height={"20"}
                  className="anticon"
                  stroke={"#FFF"}
                />
              }
            />
          )}
        </div>
      </div>
    </Spin>
  );
};

export default InstitutionForm;